import { Alert, Platform } from 'react-native'

/**
 * Utility functions for generating share URLs
 * Handles different environments (dev, staging, prod)
 */

/**
 * Get the base domain for share URLs based on environment
 */
function getProductionDomain(): string {
  const isProd = process.env.EXPO_PUBLIC_STAGE === 'prod'
  return isProd ? 'https://app.pokerpower.com' : 'https://app.dev.pokerpower.com'
}

/**
 * Get the base URL for share/QR links. Use on web (window.origin) or mobile (app domain).
 */
export function getShareBaseUrl(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.origin
  }
  return getProductionDomain()
}

function getLocalhostDomain(): string {
  return 'http://localhost:8081'
}

/**
 * Generate a share URL for joining a game by code
 * In production, always uses the universal link domain
 * In __DEV__, defaults to the universal link (works for mobile)
 *
 * @param code - The 6-character game code
 * @returns Full URL for sharing
 */
export function getShareUrl(code: string): string {
  // Always use production/staging domain - works as universal link on mobile
  const domain = getProductionDomain()
  return `${domain}/game/join/${code}`
}

/**
 * Generate a share URL for joining a lounge by code
 * Always uses the production/staging universal link domain so QR codes
 * scanned from other devices resolve correctly.
 *
 * @param code - The lounge join code
 * @returns Full URL for sharing
 */
export function getLoungeShareUrl(code: string): string {
  const domain = getProductionDomain()
  return `${domain}/lounges/join?code=${code}`
}

/**
 * Generate a localhost share URL for web development
 * @param code - The 6-character game code
 * @returns Full localhost URL for sharing
 */
export function getLocalhostShareUrl(code: string): string {
  return `${getLocalhostDomain()}/game/join/${code}`
}

/**
 * In __DEV__ mode, prompts the user to choose between web (localhost) or mobile (universal link) URL
 * In production, returns the universal link URL directly
 *
 * @param code - The 6-character game code
 * @returns Promise that resolves to the appropriate share URL
 */
export function promptShareUrl(code: string): Promise<string> {
  return new Promise((resolve) => {
    if (!__DEV__) {
      // In production, always use universal link
      resolve(getShareUrl(code))
      return
    }

    // In __DEV__ on web, always use localhost
    if (Platform.OS === 'web') {
      resolve(getLocalhostShareUrl(code))
      return
    }

    // In __DEV__ on mobile, ask user
    Alert.alert(
      'Share Link Type',
      'Who are you sharing with?',
      [
        {
          text: 'Mobile App',
          onPress: () => resolve(getShareUrl(code))
        },
        {
          text: 'Web (localhost)',
          onPress: () => resolve(getLocalhostShareUrl(code))
        }
      ],
      { cancelable: false }
    )
  })
}

/**
 * Generate a share message with code and URL
 * @param code - The 6-character game code
 * @returns Object with message and URL for sharing
 */
export function getShareContent(code: string) {
  const url = getShareUrl(code)
  return {
    url,
    message: `${code}: Join my game on Poker Power`,
    title: 'Join my Poker Power game'
  }
}

/**
 * Generate a join URL for a lounge.
 * Always uses the production/staging domain — works as a universal link on mobile.
 *
 * @param code - The lounge join code
 * @returns Full URL for sharing the lounge
 */
export function getLoungeJoinUrl(code: string): string {
  const domain = getProductionDomain()
  return `${domain}/lounges/join?code=${code}`
}
